/**
 * Hero Title Animation Class
 * Creates a stagger animation for hero title characters
 * Each character appears from top with fade effect on page load
 */
class HeroTitleAnimation {
  constructor() {
    this.elements = [];
    this.isInitialized = false;
    this.init();
  }

  init() {
    this.setup();
    this.setupResizeHandler();
  }

  setup() {
    // Find all hero title elements
    this.elements = document.querySelectorAll(".hero-title h1, .hero-title p");

    if (!this.elements.length) return;

    // Set initial hidden state immediately to prevent flash of visible text
    this.elements.forEach((element) => {
      gsap.set(element, { opacity: 0 });
    });

    // SplitText requires fonts to be loaded for correct measurements
    const runWhenReady = () => {
      if (document.fonts && typeof document.fonts.ready !== "undefined") {
        const timeout = new Promise((resolve) => setTimeout(resolve, 3000));
        Promise.race([document.fonts.ready, timeout]).then(() =>
          this.createAnimations(),
        );
      } else {
        // No Font Loading API: short delay then run (reduces layout shift)
        setTimeout(() => this.createAnimations(), 100);
      }
    };
    runWhenReady();
  }

  createAnimations() {
    // Check if we're on desktop (>= 1200px) for animations
    const isDesktop = window.innerWidth >= 1200;

    this.elements.forEach((element, index) => {
      if (isDesktop) {
        this.animateElement(element, index);
      } else {
        this.setupMobileElement(element);
      }
    });
    this.isInitialized = true;
  }

  animateElement(element, elementIndex = 0) {
    // Split text into words to preserve word boundaries and design layout
    const split = new SplitText(element, {
      type: "words",
      wordsClass: "hero-word",
      aria: "none",
    });

    // Set parent element to visible at start of animation so child words can be seen
    gsap.set(element, { opacity: 1 });

    // Set initial state - words start from above, clean and simple
    gsap.set(split.words, {
      opacity: 0,
      y: -50, // Reduced distance for smoother animation
      transformOrigin: "center center",
      force3D: true, // Enable hardware acceleration for smoother animation
    });

    // Create stagger animation timeline with word-based timing
    const tl = gsap.timeline({
      delay: elementIndex * 0.1, // Faster start between elements
    });

    // Animate words with stagger - this preserves word boundaries completely
    tl.to(split.words, {
      opacity: 1,
      y: 0,
      duration: 1.4, // Slightly longer for smoother feel
      ease: "power4.out", // Smoother easing curve
      stagger: {
        each: 0.08, // Slightly tighter stagger for smoother flow
        from: "start",
      },
      force3D: true, // Hardware acceleration
      onComplete: () => {
        // Ensure opacity is 1 after animation completes
        gsap.set(element, { opacity: 1 });
      },
    });

    // Store split instance for cleanup if needed
    element.splitInstance = split;
  }

  setupMobileElement(element) {
    // Mobile: No animation, just set final state immediately
    gsap.set(element, {
      opacity: 1,
      y: 0,
    });

    // Store a simple marker to indicate this element was processed
    element.isMobileSetup = true;
  }

  setupResizeHandler() {
    let previousViewportWidth = window.innerWidth;
    let resizeTimeout;

    const handleResize = () => {
      // Clear any existing timeout
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }

      const currentViewportWidth = window.innerWidth;

      // Debounce resize to prevent excessive calls
      resizeTimeout = setTimeout(() => {
        // Check if we've crossed the desktop/mobile threshold
        const wasDesktop = previousViewportWidth >= 1200;
        const isDesktop = currentViewportWidth >= 1200;

        if (wasDesktop !== isDesktop) {
          // Viewport category changed, refresh all animations
          this.refresh();
        }

        // Update previous width for next comparison
        previousViewportWidth = currentViewportWidth;
      }, 100);
    };

    window.addEventListener("resize", handleResize);
    this.resizeHandler = handleResize;
  }

  // Method to replay animation if needed
  replay() {
    if (!this.isInitialized) return;

    const isDesktop = window.innerWidth >= 1200;

    this.elements.forEach((element) => {
      if (isDesktop && element.splitInstance) {
        const words = element.splitInstance.words;

        // Reset to initial clean state
        gsap.set(words, {
          opacity: 0,
          y: -50,
          force3D: true,
        });

        // Animate with word-based stagger again
        gsap.to(words, {
          opacity: 1,
          y: 0,
          duration: 1.4,
          ease: "power4.out",
          stagger: {
            each: 0.08,
            from: "start",
          },
          force3D: true,
        });
      } else if (!isDesktop) {
        // Mobile: Just ensure final state
        this.setupMobileElement(element);
      }
    });
  }

  // Method to refresh animations when viewport changes
  refresh() {
    this.destroy();
    this.setup();
  }

  // Cleanup method
  destroy() {
    this.elements.forEach((element) => {
      if (element.splitInstance) {
        element.splitInstance.revert();
        delete element.splitInstance;
      }
      // Clean up mobile setup marker
      delete element.isMobileSetup;
    });
    this.elements = [];
    this.isInitialized = false;

    // Remove resize handler
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
      this.resizeHandler = null;
    }
  }
}

// Initialize hero title animation
new HeroTitleAnimation();
